import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Moon, Sun, User } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { useUserPreferences } from '@/contexts/UserPreferencesContext.js';
import { avatarColorFromName, initialsFromName } from '@/lib/theme.js';
import { prettyLabel } from '@/lib/format.js';

export function UserMenuDropdown({ storeName }: { storeName?: string | null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { preferences, profile, setTheme } = useUserPreferences();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
  const initials = initialsFromName(user?.firstName, user?.lastName);
  const color = avatarColorFromName(displayName);
  const theme = preferences?.theme ?? 'light';
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function handleSignOut() {
    clearAuth();
    navigate('/login');
  }

  async function toggleDark() {
    await setTheme(isDark ? 'light' : 'dark');
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-2.5 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: color }}
        >
          {initials}
        </span>
        <span className="hidden max-w-[8rem] truncate text-sm font-semibold text-slate-800 dark:text-slate-100 sm:inline">
          {user?.firstName || 'Account'}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700">
            <p className="font-semibold text-slate-950 dark:text-white">{displayName}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {prettyLabel(user?.role ?? '')}
              {profile?.storeName || storeName ? ` · ${profile?.storeName ?? storeName}` : ''}
            </p>
          </div>

          <div className="py-1">
            <Link
              to="/settings/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <User className="h-4 w-4" /> My Profile
            </Link>
            <Link
              to="/settings/profile#notifications"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Bell className="h-4 w-4" /> Notifications
            </Link>
            <Link
              to="/settings/profile#security"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <span className="inline-flex h-4 w-4 items-center justify-center text-xs">🔢</span>
              Set PIN
            </Link>
          </div>

          <div className="border-t border-slate-100 py-1 dark:border-slate-700">
            <button
              type="button"
              role="menuitem"
              onClick={() => void toggleDark()}
              className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <span className="flex items-center gap-2">
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                Dark Mode
              </span>
              <span
                className={[
                  'relative h-5 w-9 rounded-full transition',
                  isDark ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600',
                ].join(' ')}
              >
                <span
                  className={[
                    'absolute top-0.5 h-4 w-4 rounded-full bg-white transition',
                    isDark ? 'left-4' : 'left-0.5',
                  ].join(' ')}
                />
              </span>
            </button>
          </div>

          <div className="border-t border-slate-100 py-1 dark:border-slate-700">
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
